import React, { useMemo, useCallback, useState } from 'react';
import type { SingleValue } from 'react-select';

import type { SingleValueProps, OptionProps, OptionType } from '@atlaskit/select/types';
import Select from '@atlaskit/select/select';
import { components } from '@atlaskit/select/react-select';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { layers } from '@atlaskit/theme/constants';
import { cssMap } from '@atlaskit/css';
import { defineMessages, useIntl } from 'react-intl';

import type { SpaceRole } from '../model/space-roles-types.ts';

const i18n = defineMessages({
	noRolesAvailable: {
		id: 'space-roles.role-selector.no-roles-available',
		defaultMessage: 'No roles are currently available',
		description:
			'No options message shown when no roles are returned — for example, because all assignable roles have been disabled by an admin. This is a configuration state, not a permissions error.',
	},
});

interface Option {
	id: string;
	label: string;
	value: string;
	description: string;
	ariaLabel: string;
	optionColor?: React.ComponentProps<typeof Text>['color'];
}

interface RoleSelectorProps {
	initialRoleId?: string | null;
	value?: string | null | OptionType;
	isLoading?: boolean;
	isDisabled?: boolean;
	placeholder?: React.ReactNode;
	onChange?: (role: SpaceRole) => void;
	spaceRoleOptions: (Pick<SpaceRole, 'id' | 'name' | 'description' | 'roleType'> & {
		/** Optional color token applied to both the option label and description text. Defaults to standard text colors. */
		optionColor?: React.ComponentProps<typeof Text>['color'];
	})[];
	onMenuOpen?: () => void;
	onMenuClose?: () => void;
	menuPlacement?: 'auto' | 'bottom' | 'top';
	appearance?: 'none' | 'default' | 'subtle';
	spacing?: 'default' | 'compact';
	noOptionsMessage?: string | null;
	menuPortalTarget?: HTMLElement;
	/**
	 * Maximum height of the dropdown menu in pixels.
	 */
	maxMenuHeight?: number;
	/**
	 * Function to render additional content (like a lozenge) next to a role option.
	 * Returns a React node for the given role ID, or null/undefined to render nothing.
	 */
	renderOptionSuffix?: (roleId: string) => React.ReactNode;
	/**
	 * Optional footer content to render at the bottom of the menu dropdown.
	 * The footer is sticky and stays visible while scrolling through options.
	 * Can be a React node or a render function that receives a closeMenu callback.
	 */
	menuFooter?: React.ReactNode | ((closeMenu: () => void) => React.ReactNode);
	/**
	 * Id for the underlying combobox input. Use with a matching <label htmlFor="..."> for accessibility.
	 */
	inputId?: string;
}

const styles = cssMap({
	menuItem: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	menuContainer: {
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '320px',
	},
	menuScrollable: {
		flex: 1,
		overflowY: 'auto',
		minHeight: '0px',
	},
	optionWithSuffixOuter: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		minWidth: '0px',
		width: '100%',
	},
	optionWithSuffixCluster: {
		display: 'inline-flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		maxWidth: '100%',
		minWidth: '0px',
	},
	optionLabelTruncate: {
		minWidth: '0px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: '0 1 auto',
	},
	optionSuffixContainer: {
		flexShrink: 0,
	},
});

export const RoleSelector: React.FC<RoleSelectorProps> = ({
	initialRoleId,
	value,
	isLoading,
	isDisabled = false,
	onChange,
	placeholder,
	spaceRoleOptions,
	onMenuClose,
	onMenuOpen,
	menuPlacement = 'auto',
	appearance = 'subtle',
	noOptionsMessage,
	spacing = 'default',
	menuPortalTarget,
	maxMenuHeight = 320,
	renderOptionSuffix,
	menuFooter,
	inputId,
}) => {
	const { formatMessage } = useIntl();
	const [internalVal, setInternalVal] = useState<string | OptionType | null>(
		(value === undefined ? initialRoleId : value) || null,
	);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const resolvedNoOptionsMessage = noOptionsMessage ?? formatMessage(i18n.noRolesAvailable);

	const closeMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	// The component is controlled if value is not `undefined`. `initialRole` will ignored in controlled mode.
	const isControlledComponent = value !== undefined;

	const options: Option[] = useMemo(
		() =>
			spaceRoleOptions.map((role) => ({
				id: role?.id ?? '',
				label: role?.name ?? '',
				value: role?.id ?? '',
				description: role?.description ?? '',
				ariaLabel: `${role?.name} - ${role?.description ?? ''}`,
				optionColor: role?.optionColor,
			})),
		[spaceRoleOptions],
	);

	const getOptionFromValue = useCallback(
		(val: OptionType | string | null | undefined): Option | null => {
			if (!val) return null;

			return typeof val === 'string'
				? options.find((option) => option.value === val) || null
				: {
						id: val.value.toString(),
						label: val.label,
						value: val.value.toString(),
						description: '',
						ariaLabel: `${val.label} - ${val.description ?? ''}`,
					};
		},
		[options],
	);

	const CustomSingleValue = React.memo((props: SingleValueProps<Option>) => {
		const suffix = renderOptionSuffix?.(props.data.id);
		return (
			<components.SingleValue {...props} aria-label={props.data.ariaLabel}>
				{suffix ? (
					<Box xcss={styles.optionWithSuffixOuter}>
						<Box xcss={styles.optionWithSuffixCluster}>
							<Box xcss={styles.optionLabelTruncate}>{props.data.label}</Box>
							<Box xcss={styles.optionSuffixContainer}>{suffix}</Box>
						</Box>
					</Box>
				) : (
					props.data.label
				)}
			</components.SingleValue>
		);
	});

	const CustomOption = React.memo((props: OptionProps<Option>) => {
		const suffix = renderOptionSuffix?.(props.data.id);
		const optionColor = props.data.optionColor;
		return (
			<components.Option {...props} aria-label={props.data.ariaLabel}>
				<Box xcss={styles.menuItem}>
					{suffix ? (
						<Box xcss={styles.optionWithSuffixOuter}>
							<Box xcss={styles.optionWithSuffixCluster}>
								<Box xcss={styles.optionLabelTruncate}>
									<Text color={optionColor}>{props.data.label}</Text>
								</Box>
								<Box xcss={styles.optionSuffixContainer}>{suffix}</Box>
							</Box>
						</Box>
					) : (
						<Text color={optionColor}>{props.data.label}</Text>
					)}
					<Text color={optionColor ?? 'color.text.subtlest'}>{props.data.description}</Text>
				</Box>
			</components.Option>
		);
	});

	// Resolve menuFooter - call it if it's a function
	const resolvedMenuFooter = useMemo(() => {
		if (!menuFooter) return null;
		return typeof menuFooter === 'function' ? menuFooter(closeMenu) : menuFooter;
	}, [menuFooter, closeMenu]);

	// Custom Menu component with optional footer
	const CustomMenu = useMemo(() => {
		if (!resolvedMenuFooter) return undefined;

		return React.memo((props: any) => (
			<components.Menu {...props}>
				<Box xcss={styles.menuContainer}>
					<Box xcss={styles.menuScrollable}>{props.children}</Box>
					{resolvedMenuFooter}
				</Box>
			</components.Menu>
		));
	}, [resolvedMenuFooter]);

	const handleChange = (newSelection: SingleValue<Option>) => {
		const newSelectionType = spaceRoleOptions.find(
			(role) => role?.id === newSelection?.value,
		)?.roleType;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'change',
				source: 'spaceRoles-roleSelector',
				attributes: {
					newSelection: newSelection?.id,
					newSelectionType,
				},
			},
		}).fire();

		setInternalVal(newSelection?.value || null);
		// Guard the unsafe `as SpaceRole` cast: if the option list and the new selection ever
		// diverge (e.g. a stale option fires after the list was filtered, or a disabled SYSTEM
		// role is no longer in the list), `.find(...)` returns undefined, and downstream
		// consumers that do `newRole.id` would NPE. Mirrors the guard pattern used by the
		// site-permissions local copy of this component.
		const matchedRole = spaceRoleOptions.find((role) => role?.id === newSelection?.value);
		if (matchedRole) {
			onChange?.(matchedRole as SpaceRole);
		}
	};

	const handleMenuOpen = useCallback(
		(currentRoleSelection: Option | null) => {
			onMenuOpen?.();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'open',
					source: 'spaceRoles-roleSelector',
					attributes: { currentRoleSelection: currentRoleSelection?.id },
				},
			}).fire();
		},
		[createAnalyticsEvent, onMenuOpen],
	);

	const SELECT_STYLE_PROPS = {
		isSearchable: false,
		styles: {
			menu: (base: any) => ({
				...base,
				width: '320px',
				padding: 0,
				margin: 0,
				zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
			}),
			menuPortal: (base: any) => ({
				...base,
				zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
			}),
		},
	};

	const selectValue = isControlledComponent
		? getOptionFromValue(value)
		: getOptionFromValue(internalVal);

	const handleMenuOpenInternal = useCallback(() => {
		setIsMenuOpen(true);
		handleMenuOpen(getOptionFromValue(isControlledComponent ? value : internalVal));
	}, [getOptionFromValue, handleMenuOpen, isControlledComponent, value, internalVal]);

	const handleMenuCloseInternal = useCallback(() => {
		setIsMenuOpen(false);
		onMenuClose?.();
	}, [onMenuClose]);

	return (
		<Select
			data-testid="space-roles-table-role-selector"
			inputId={inputId}
			placeholder={isLoading ? '' : placeholder}
			options={options}
			appearance={appearance}
			isLoading={isLoading}
			isDisabled={isDisabled}
			onChange={handleChange}
			onMenuOpen={handleMenuOpenInternal}
			onMenuClose={handleMenuCloseInternal}
			menuIsOpen={isMenuOpen}
			components={{
				SingleValue: CustomSingleValue,
				Option: CustomOption,
				...(CustomMenu ? { Menu: CustomMenu } : {}),
			}}
			menuPosition="fixed"
			menuPlacement={menuPlacement}
			menuPortalTarget={menuPortalTarget}
			maxMenuHeight={maxMenuHeight}
			scrollBehavior="outside"
			defaultValue={getOptionFromValue(initialRoleId)}
			value={selectValue}
			noOptionsMessage={() => resolvedNoOptionsMessage}
			spacing={spacing}
			{...SELECT_STYLE_PROPS}
		/>
	);
};
